import { useState, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { springs } from '../lib/animations';

interface ReactionBarProps {
  ratingId: number;
  username: string;
  initialReactions?: Record<string, number>;
  initialUserReactions?: string[];
}

const REACTION_TYPES = [
  { type: 'fire', label: 'Fire' },
  { type: 'trash', label: 'Trash' },
  { type: 'respect', label: 'Respect' },
] as const;

export default function ReactionBar({
  ratingId,
  username,
  initialReactions = {},
  initialUserReactions = [],
}: ReactionBarProps) {
  const [reactions, setReactions] = useState<Record<string, number>>(initialReactions);
  const [userReactions, setUserReactions] = useState<string[]>(initialUserReactions);
  const [pending, setPending] = useState<string | null>(null);

  const handleReact = useCallback(async (type: string) => {
    if (!username || pending) return;

    const isActive = userReactions.includes(type);
    const prevReactions = { ...reactions };
    const prevUserReactions = [...userReactions];

    // Optimistic update
    setReactions(prev => ({
      ...prev,
      [type]: Math.max(0, (prev[type] || 0) + (isActive ? -1 : 1)),
    }));
    setUserReactions(prev =>
      isActive ? prev.filter(r => r !== type) : [...prev, type]
    );

    setPending(type);
    try {
      const res = await fetch(`/api/rating/${ratingId}/react`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ username, type }),
      });
      if (!res.ok) throw new Error('Failed');
      const data = await res.json();
      setReactions(data.reactions);
      setUserReactions(data.userReactions);
    } catch {
      // Revert on failure
      setReactions(prevReactions);
      setUserReactions(prevUserReactions);
    } finally {
      setPending(null);
    }
  }, [ratingId, username, reactions, userReactions, pending]);

  return (
    <div className="flex items-center gap-2 justify-center">
      {REACTION_TYPES.map(({ type, label }) => {
        const isActive = userReactions.includes(type);
        const count = reactions[type] || 0;
        return (
          <motion.button
            key={type}
            onClick={() => handleReact(type)}
            disabled={!username || pending === type}
            title={label}
            className={`
              flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium
              transition-all duration-200 select-none
              ${isActive
                ? 'bg-warm/20 border border-warm/40 text-cream shadow-glow-warm scale-105'
                : 'bg-cannabis-800 border border-cannabis-700/30 text-cream/60 hover:border-warm/25 hover:text-cream hover:bg-cannabis-700'
              }
              ${pending === type ? 'opacity-60 cursor-wait' : ''}
              disabled:opacity-40 disabled:cursor-not-allowed
            `}
            whileTap={{ scale: 1.3 }}
            transition={springs.bouncy}
          >
            <span className="text-xs font-medium">{label}</span>
            <AnimatePresence mode="wait">
              <motion.span
                key={count}
                initial={{ opacity: 0, y: -5 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: 5 }}
                transition={{ duration: 0.15 }}
              >
                {count}
              </motion.span>
            </AnimatePresence>
          </motion.button>
        );
      })}
    </div>
  );
}
